import mixitup from 'mixitup'
import mixitupMultiFilter from './mixitup-multifilter.min.js'

const quizzes = document.querySelectorAll('.quiz') as NodeListOf<HTMLDivElement>
const games = document.querySelectorAll('.game') as NodeListOf<HTMLDivElement>
const quizCards = document.querySelectorAll('.card__quiz-page') as NodeListOf<HTMLAnchorElement>

/**
 * Check if the answer is correct
 * @param el the button representing the option/answer in quiz
 * @returns true or false
 */
const isCorrect = (el: HTMLButtonElement):boolean => {
    return el.getAttribute('data-correct') == 'true'
}

/**
 * Toggle pointer events on/off on quiz options
 * @param value "none" or "all"
 * @param slide the slide on which to modify pointer events
 */
const toggleButtonEvents = (value: string, slide: HTMLDivElement) => {
    slide.querySelectorAll('.quiz__slide--question__options__option').forEach(option => {
        let btn = option as HTMLButtonElement
        btn.style.pointerEvents = value
    })
}

/**
 * Reveal the correct answer, add points if user answered correct
 * @param quiz current quiz being played
 * @param slide slide/question to show answer for 
 * @param correct if the user answered correctly
 */
const showAnswer = (quiz: HTMLDivElement, slide: HTMLDivElement, correct: boolean) => {
    let answerSlide = slide.querySelector('.quiz__slide--answer') as HTMLDivElement
    slide.classList.replace('hide-answer', 'show-answer')

    if (correct) {
        answerSlide.setAttribute('data-result', 'correct')
        let newScore = parseInt(quiz.getAttribute('data-score')) + 1
        quiz.setAttribute('data-score', newScore.toString())
    }
    else {
        answerSlide.setAttribute('data-result', 'incorrect')
    }

    toggleButtonEvents('none', slide)
}

/**
 * Show next slide/question
 * @param quiz current quiz being played
 * @param slide current slide
 */
const showNextSlide = (quiz: HTMLDivElement, slide: HTMLDivElement) => {
    let nextSlideIndex = parseInt(slide.getAttribute('data-index')) + 1
    let nextSlide = quiz.querySelector('[data-index="' + nextSlideIndex + '"]') as HTMLDivElement

    if (nextSlide) {
        slide.classList.remove('active')
        nextSlide.classList.add('active')

        setTimeout(() => {
            toggleButtonEvents('all', nextSlide)
        }, 400)

        if (nextSlide.classList.contains('last-slide')) {
            let scoreContainer = nextSlide.querySelector('.quiz-score') as HTMLSpanElement
            scoreContainer.innerText = quiz.getAttribute('data-score')
        }
    }

    setTimeout(() => { window.scroll(0, 0) }, 10)
}

/**
 * Reset quiz (score is 0, back to start)
 * @param quiz quiz to reset
 */
const resetQuiz = (quiz: HTMLDivElement) => {
    quiz.setAttribute('data-score', '0')

    let lastSlide = quiz.querySelector('.last-slide') as HTMLDivElement
    let firstSlide = quiz.querySelector('.first-slide') as HTMLDivElement

    quiz.querySelectorAll('.quiz__slide').forEach(slide => {
        let s = slide as HTMLDivElement
        s.classList.replace('show-answer', 'hide-answer')
        s.classList.remove('active')
        toggleButtonEvents('none', s)
    })

    if (lastSlide) lastSlide.classList.remove('active')
    if (firstSlide) {
        firstSlide.classList.add('active')
        toggleButtonEvents('all', firstSlide)
    }
}

/**
 * Show next quiz
 * @param quiz current quiz
 */
const showNextQuiz = (quiz: HTMLDivElement) => {
    let nextIndex = !quiz.classList.contains('last-quiz') ? (parseInt(quiz.getAttribute('data-quiz-index')) + 1) : 0
    let next = document.querySelector('[data-quiz-index="' + nextIndex + '"]') as HTMLDivElement

    if (next) {
        quiz.classList.remove('active')
        next.classList.add('active')

        displayQuiz(next.id)
        resetQuiz(quiz)
        resetQuiz(next)
    }
}

/**
 * Hide all quizzes
 */
const hideQuizzes = () => {
    quizzes.forEach(quiz => {
        quiz.classList.remove('active')
        resetQuiz(quiz)
    })
    games.forEach(game => {
        game.classList.remove('active')
    })
}

/**
 * Display a quiz
 * @param id id of quiz to show
 */
const displayQuiz = (id: string) => {
    hideQuizzes()

    let quizId = '#' + id

    if (quizId.indexOf('##') < 1) {
        quizId = quizId.replace('##', '#')
    }

    let quizToShow = document.querySelector(quizId)

    const searchString = window.location.hash
    const filterString = searchString.split('?')[1]

    if (quizToShow) {
        quizToShow.classList.add('active')
        const stateString = quizId + '?' + filterString
        window.history.replaceState({}, '', stateString)
    } else {
        let firstQuiz = document.querySelector('[data-quiz-index="0"]') as HTMLDivElement
        
        if (firstQuiz) {
            let firstQuizId = '#' + firstQuiz.id
            firstQuiz.classList.add('active')
            const stateString = firstQuizId + '?' + filterString
            window.history.replaceState({}, '', stateString)
        }
    }

    setTimeout(() => { window.scroll(0, 0) }, 10)
}

/**
 * Initialise quiz functionality
 */
const initQuizzes = () => {
    quizzes.forEach(q => {
        let quiz = q as HTMLDivElement

        quiz.querySelectorAll('.quiz__slide').forEach(s => {
            let slide = s as HTMLDivElement
            let answerOptions = slide.querySelectorAll('.quiz__slide--question__options__option') as NodeListOf<HTMLButtonElement>

            answerOptions.forEach(option => {
                option.addEventListener('click', () => {
                    showAnswer(quiz, slide, isCorrect(option))
                    setTimeout(() => { window.scroll(0, 0) }, 10)
                })
            })

            let nextQuestion = slide.querySelector('.next-question') as HTMLButtonElement
            if (nextQuestion) nextQuestion.addEventListener('click', () => { showNextSlide(quiz, slide) })

            let nextQuiz = slide.querySelector('.next-quiz') as HTMLButtonElement
            if (nextQuiz) nextQuiz.addEventListener('click', () => { showNextQuiz(quiz) })

            let tryAgain = slide.querySelector('.try-again') as HTMLButtonElement
            if (tryAgain) tryAgain.addEventListener('click', () => { resetQuiz(quiz) })
        })
    })
}

/**
 * Initialise link card listeners
 */
const initQuizLinks = () => {
    if (quizCards.length > 0) {
        quizCards.forEach(card => {
            const cardId = card.getAttribute('data-id-for')

            card.addEventListener('click', () => {
                displayQuiz(cardId)
            })
        })
    }
}

/**
 * Display quiz when page loads
 */
const showQuizOnLoad = () => {
    const str = window.location.hash
    const match = RegExp(/^#[^?]+/).exec(str)
    let result = match ? match[0] : ''

    if (result != '') {
        displayQuiz(result)
    }
    else if (quizzes.length > 0) {
        result = quizzes[0].id
        displayQuiz(result)
    }
}

/** MIXER IN QUERYSTRING FUNCTIONS */

/**
 * Define query string object
 */
interface QueryStringObject {
    filter?: string
}

/**
 * Create an instance of QueryStringObject from a string
 * @param str = input string (search string)
 * @returns an instance of QueryStringObject
 */
const createQueryObjectFromString = (str: string): QueryStringObject => {
    let query: QueryStringObject = {
        filter: ''
    }

    let splitString = str.split('&')

    splitString.forEach((s) => {
        if (s.includes('filter')) query.filter = s.split('=')[1]
    })

    return query
}

/**
 * Create a searchstring from a QueryStringObject object
 * @param query = an instance of QueryStringObject
 * @returns a search string
 */
const createSearchString = (query: QueryStringObject): string => {
    let str = ''

    str += (query.filter != '') ? '&filter=' + query.filter : ''

    str = str.replace('&', '?')

    return str
}

/**
 * Update the search string when a filter is toggled 
 */
const updateSearchString = () => {
    let queryString = ''

    let activeFilters = document.querySelectorAll('.quiz-page__nav__item.active')

    let filterString = ''

    if (activeFilters.length > 0) activeFilters.forEach((item) => { filterString += item.getAttribute('data-label-clean') })

    if (activeFilters.length > 0)
    {
        let queryObject: QueryStringObject = {
            filter: filterString.replace(/,$/, '')
        }
        const windowHash = window.location.hash
        const match = RegExp(/^#[^?]+/).exec(windowHash)
        const hash = match ? match [0] : ''
        queryString = hash
        queryString += createSearchString(queryObject)
        window.history.replaceState({}, '', queryString)
    }
    else
    {
        let queryObject: QueryStringObject = {
            filter: 'latest'
        }

        const windowHash = window.location.hash
        const match = RegExp(/^#[^?]+/).exec(windowHash)
        const hash = match ? match [0] : ''
        queryString = hash
        queryString += createSearchString(queryObject)
        window.history.replaceState({}, '', queryString)
    }
}

/**
 * Activate filters on load
 * @param mixer = an instance of a mixitup mixer
 */
const activateFilters = (mixer: any) => {
    const str = window.location.hash
    const match = RegExp(/\?filter=[^?]+/).exec(str)
    const result = match ? match[0] : ''

    if (result.length > 1) {
        let initialQuery: QueryStringObject = createQueryObjectFromString(window.location.hash)

        if (mixer != null) {
            let filtersArr = initialQuery.filter.split(',')
            let filtersSelectorsArr: string[] = []
            if (filtersArr[0] != '') filtersArr.forEach((filter) => { filtersSelectorsArr.push('[data-filter-string~=' + filter + ']') })
            mixer.setFilterGroupSelectors('filter', filtersSelectorsArr)

            mixer.parseFilterGroups()
        }
    }
    else {
        let filterLatest = document.querySelector('[data-filter-string~=latest]')
        mixer.setFilterGroupSelectors('filter', ['[data-filter-string~=latest]'])
        mixer.parseFilterGroups()
        
        if (filterLatest)
        {
            filterLatest.classList.add('active')
        }
    }
}

/**
 * Update links on quiz/game cards
 */
const updateHref = () => {
    const allCards = document.querySelectorAll('a.card__quiz-page:not(.card__quiz-page--farm)') as NodeListOf<HTMLAnchorElement>
    const searchString = window.location.hash
    const match = RegExp(/\?filter=[^?]+/).exec(searchString)

    console.log('match', match)
    const result = match ? match[0] : ''

    allCards.forEach(card => {
        const oldHref = card.getAttribute('data-id-for')
        const newHref = '#' + oldHref + result
        card.href = newHref    
    })
}

/**
 * Function to call on mix end
 */
const mixEndFunction = () => {
    updateSearchString()
    updateHref()
}


/**
 * Initialise quiz filtering
 */
const initFilters = () => {
    const mixContainer = document.querySelector('.quiz-page__content__section')
    mixitup.use(mixitupMultiFilter)
    
    const mixer = mixitup(mixContainer, {
        selectors: {
            target: '.mix'
        },
        animation: {
            enable: false
        },
        multifilter: {
            enable: true
        },
        classNames: {
            block: '',
            elementFilter: ''
        },
        callbacks: {
            onMixEnd: mixEndFunction
        }
    })

    activateFilters(mixer)
}

export function initQuizPage() {
    initQuizzes()
    initQuizLinks()
    showQuizOnLoad()
    initFilters()
}